import { FC, useEffect, useState, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { Loading } from "../../components/Loading";
import "./index.css";
import Swal from "sweetalert2";
import { useAppConfig } from "../../hooks/useAppConfig";
import { ConfigType } from "../../contexts/AppConfigContext";
import { Menu } from "../../components/Menu";
import { Header } from "../../components/Header";
import { LeftMenu } from "../../components/LeftMenu";
import { useSeller } from "../../hooks/useSeller";
import { SellerQueueCard } from "../../components/SellerQueueCard";
import { useSellerQueue } from "../../hooks/useSellerQueue";
import { useSellerSession, NoSaleFormData } from "../../hooks/useSellerSession";
import { useSellerQueueRealtime, SellerQueueRealtimeEvent } from "../../hooks/useSellerQueueRealtime";
import { AttendanceModal } from "../../components/AttendanceModal";
import { useSellerQueueContext } from "../../contexts/SellerQueueContext";
import {
  closeSellerAttendance,
  getCurrentSession as getCurrentSellerSession,
  returnSellerToQueueSeller,
} from "../../services/sellerQueueService";

const SellerAttendance: FC = () => {
  const { getSellerAppConfig } = useAppConfig();
  const { getTokenSeller } = useSeller();
  const [loadingHeader, setLoadingHeader] = useState(true);
  const [config, setConfig] = useState<ConfigType | void>();

  const queueHook = useSellerQueue();
  const { queueEntry, isLoading: isLoadingQueue, error: errorQueue, refetchQueue } = queueHook;
  const { enterQueue, leaveQueue } = queueHook;

  const sessionHook = useSellerSession();
  const { currentSession, startService, getElapsedSeconds, refetchSession } = sessionHook;

  useEffect(() => {
    refetchQueue();
    refetchSession();
  }, [refetchQueue, refetchSession]);

  const { setQueueEntry } = useSellerQueueContext();

  const [showEndServiceModal, setShowEndServiceModal] = useState(false);
  const [serviceEndIntent, setServiceEndIntent] = useState<"return_to_queue" | "end_and_leave_queue" | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [notification, setNotification] = useState<{ type: "success" | "error"; message: string } | null>(null);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [attendanceMenuOpen, setAttendanceMenuOpen] = useState(false);
  const queueEntryRef = useRef(queueEntry);

  useEffect(() => {
    queueEntryRef.current = queueEntry;
  }, [queueEntry]);

  const showNotification = (type: "success" | "error", message: string) => {
    setNotification({ type, message });
    setTimeout(() => setNotification(null), 4000);
  };

  const getApiErrorMessage = (error: unknown, fallback: string) => {
    const maybeResponse = (error as any)?.response?.data;
    return (
      maybeResponse?.message ||
      maybeResponse?.error ||
      (error instanceof Error ? error.message : null) ||
      fallback
    );
  };

  const handleRealtimeEvent = useCallback((event: SellerQueueRealtimeEvent) => {
    console.log("[SellerAttendance] Real-time event:", event);

    if (event.type === "called") {
      showNotification("success", "📞 Você foi chamado! Clique em 'Iniciar Atendimento'");
    } else if (event.type === "position-updated") {
      const currentQueueEntry = queueEntryRef.current;
      if (event.data && currentQueueEntry) {
        setQueueEntry({
          ...currentQueueEntry,
          position: event.data.position,
        });
      }
    } else if (event.type === "status-changed") {
      const currentQueueEntry = queueEntryRef.current;
      if (event.data && currentQueueEntry) {
        setQueueEntry({
          ...currentQueueEntry,
          status: event.data.status,
        });
      }
    }
  }, [setQueueEntry]);
  useSellerQueueRealtime(handleRealtimeEvent);

  useEffect(() => {
    if (queueEntry?.status !== "in_service") return;
    if (currentSession?.status === "in_progress") return;

    refetchSession();
  }, [queueEntry?.status, currentSession?.status, refetchSession]);

  useEffect(() => {
    if (!currentSession || currentSession.status !== "in_progress") {
      setElapsedTime(0);
      return;
    }

    setElapsedTime(getElapsedSeconds());

    const interval = setInterval(() => {
      setElapsedTime(getElapsedSeconds());
    }, 1000);

    return () => clearInterval(interval);
  }, [currentSession, getElapsedSeconds]);

  const getConfig = useCallback(async () => {
    try {
      setLoadingHeader(true);
      const sellerConfig = await getSellerAppConfig();
      setConfig(sellerConfig);
      setLoadingHeader(false);
    } catch (error) {
      console.error("Error loading seller config:", error);
      setLoadingHeader(false);
    }
  }, [getSellerAppConfig]);

  useEffect(() => {
    getConfig();
  }, [getConfig]);

  const handleEnterQueue = async () => {
    try {
      setIsProcessing(true);
      await enterQueue();
      showNotification("success", "✓ Você entrou na fila");
    } catch (error) {
      showNotification(
        "error",
        `Erro ao entrar na fila: ${error instanceof Error ? error.message : "Erro desconhecido"}`
      );
    } finally {
      setIsProcessing(false);
    }
  };

  const handleLeaveQueue = async () => {
    const result = await Swal.fire({
      title: "Sair da Fila",
      text: "Tem certeza que deseja sair da fila de atendimento?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sim, sair",
      cancelButtonText: "Cancelar",
    });

    if (!result.isConfirmed) return;

    try {
      setIsProcessing(true);
      await leaveQueue();
      showNotification("success", "✓ Você saiu da fila");
    } catch (error) {
      showNotification(
        "error",
        `Erro ao sair da fila: ${error instanceof Error ? error.message : "Erro desconhecido"}`
      );
    } finally {
      setIsProcessing(false);
    }
  };

  const handleStartService = async () => {
    if (!queueEntry?.id) return;

    try {
      setIsProcessing(true);
      await startService(queueEntry.id);
      showNotification("success", "🎯 Atendimento iniciado!");
    } catch (error) {
      showNotification(
        "error",
        `Erro ao iniciar: ${error instanceof Error ? error.message : "Erro desconhecido"}`
      );
    } finally {
      setIsProcessing(false);
    }
  };

  const handleReturnToQueueClick = () => {
    setServiceEndIntent("return_to_queue");
    setShowEndServiceModal(true);
  };

  const handleEndAndLeaveQueueClick = () => {
    setServiceEndIntent("end_and_leave_queue");
    setShowEndServiceModal(true);
  };

  const processServiceEnd = async (outcome: string, noSaleData?: NoSaleFormData) => {
    if (!serviceEndIntent) {
      showNotification("error", "Selecione a ação de encerramento novamente");
      return;
    }

    const sellerId = currentSession?.sellerId || tokenSeller?.id;
    let sessionId = currentSession?.id;

    if (!sessionId && tokenSeller?.pluginspaceId && sellerId) {
      const latestSession = await getCurrentSellerSession(tokenSeller.pluginspaceId, sellerId);
      sessionId = latestSession?.id;
    }

    if (!sessionId || !sellerId) {
      showNotification("error", "Nenhum atendimento em progresso para encerrar");
      return;
    }

    if (outcome !== "sale_completed" && outcome !== "no_sale") {
      showNotification("error", "Resultado inválido para encerramento");
      return;
    }

    try {
      setIsProcessing(true);

      const reasonCode = outcome === "no_sale" ? noSaleData?.reasonCode ?? undefined : undefined;
      const note = noSaleData?.note;

      if (serviceEndIntent === "end_and_leave_queue") {
        await closeSellerAttendance(sessionId, sellerId, outcome, reasonCode, note);
        showNotification("success", "✓ Atendimento encerrado e vendedor removido da fila");
      } else {
        await returnSellerToQueueSeller(sessionId, outcome, reasonCode, note);
        showNotification("success", "✓ Atendimento encerrado e vendedor retornou para a fila");
      }

      await Promise.all([refetchSession(), refetchQueue()]);

      setShowEndServiceModal(false);
      setServiceEndIntent(null);
    } catch (error) {
      showNotification(
        "error",
        `Erro ao encerrar: ${getApiErrorMessage(error, "Erro desconhecido")}`
      );
    } finally {
      setIsProcessing(false);
    }
  };

  const handleOutcomeSelected = async (outcome: string) => {
    await processServiceEnd(outcome);
  };

  const handleNoSaleSubmit = async (data: NoSaleFormData) => {
    await processServiceEnd("no_sale", data);
  };

  const formatTime = (seconds: number) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    if (h > 0) return `${h}h ${m}m`;
    if (m > 0) return `${m}m ${s}s`;
    return `${s}s`;
  };

  const tokenSeller = getTokenSeller();
  if (!tokenSeller) {
    return (
      <div className="sellerAttendanceHome">
        <div className="content-attendance">
          <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "100vh" }}>
            <p style={{ color: "#999", fontSize: "1em" }}>Erro: Vendedor não autenticado</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="sellerAttendanceHome">
      {attendanceMenuOpen && typeof document !== "undefined"
        ? createPortal(
            <>
              <div
                className="attendance-menu-overlay"
                onClick={() => setAttendanceMenuOpen(false)}
              />
              <LeftMenu
                extraSpacingLeftMenu
                cbSelected={(selected) => {
                  if (selected) {
                    setAttendanceMenuOpen(false);
                  }
                }}
              />
            </>,
            document.body
          )
        : null}

      <div className="content-attendance">
        {!loadingHeader && (
          <Header
            background={
              config?.webappcolor.backgroundheader ? config.webappcolor.backgroundheader : "#F5F5F5"
            }
            colorIcon={config?.webappcolor.iconsheader ? config.webappcolor.iconsheader : "#636363"}
            logo={config?.icon ? config.icon : ""}
            backgroundCart={config?.webappcolor.backgroundcart ? config.webappcolor.backgroundcart : "#FFBA49"}
            fontCart={config?.webappcolor.fontcart ? config.webappcolor.fontcart : "#000"}
            noSales={
              config && config.content && config.content.pdv && config.content.pdv.noSales
                ? config.content.pdv.noSales
                : false
            }
            extraSpacingLeftMenu
            hideLeftMenu
            leftMenuAction={() => setAttendanceMenuOpen(true)}
          />
        )}

        {loadingHeader ? (
          <div style={{ width: "100%", height: "calc(100vh)", display: "flex", justifyContent: "center", alignItems: "center" }}>
            <Loading type="spinningBubbles" color="#FFF" width={64} height={64} />
          </div>
        ) : (
          <div className="attendance-values">
            {notification && (
              <div className={`notification notification-${notification.type}`}>
                {notification.message}
              </div>
            )}

            <div className="page-header">
              <h1>Fila de Atendimento</h1>
              {currentSession?.status === "in_progress" && <div className="timer-display">⏱ {formatTime(elapsedTime)}</div>}
            </div>

            <SellerQueueCard
              queueEntry={queueEntry}
              isLoading={isLoadingQueue}
              error={errorQueue}
              elapsedTime={elapsedTime}
              onEnterQueue={handleEnterQueue}
              onLeaveQueue={handleLeaveQueue}
              onStartService={handleStartService}
              onReturnToQueue={handleReturnToQueueClick}
              onEndService={handleEndAndLeaveQueueClick}
              isServiceActive={currentSession?.status === "in_progress"}
              isLeaveLoading={isProcessing}
              isStartServiceLoading={isProcessing}
            />

            <AttendanceModal
              isOpen={showEndServiceModal}
              isLoading={isProcessing}
              onClose={() => {
                setShowEndServiceModal(false);
                setServiceEndIntent(null);
              }}
              onOutcomeSelected={handleOutcomeSelected}
              onNoSaleSubmit={handleNoSaleSubmit}
            />
          </div>
        )}

        {!loadingHeader && (
          <Menu
            activeMenu="attendance"
            fontColor={config?.webappcolor.iconsfooter ? config.webappcolor.iconsfooter : "#636363"}
            activeFontColor={config?.webappcolor.highlightfooter ? config.webappcolor.highlightfooter : "#000"}
            backgroundMenu={config?.webappcolor.backgroundfooter ? config.webappcolor.backgroundfooter : "#F5F5F5"}
            colorIcons={config?.webappcolor.iconsfooter ? config.webappcolor.iconsfooter : "#636363"}
            highlightColorIcons={config?.webappcolor.highlightfooter ? config.webappcolor.highlightfooter : "#636363"}
            backgroundCart={config?.webappcolor.backgroundcart ? config.webappcolor.backgroundcart : "#FFBA49"}
            fontCart={config?.webappcolor.fontcart ? config.webappcolor.fontcart : "#000"}
            noSales={
              config && config.content && config.content.pdv && config.content.pdv.noSales
                ? config.content.pdv.noSales
                : false
            }
          />
        )}
      </div>
    </div>
  );
};

export { SellerAttendance };
